"use client";

import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import type { DataTablePaginationProps } from "./types";

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 20, 50, 100],
  className,
}: DataTablePaginationProps<TData>) {
  return (
    <div
      className={cn(
        // No `bg-panel` + `shadow-raised-up` here: the row already sits inside
        // the table's panel, and a panel inside a panel is invisible (§2).
        "flex h-control-lg shrink-0 items-center justify-end gap-2 px-4",
        "text-xs tabular-nums text-text-tertiary",
        className,
      )}
    >
      {table.getFilteredSelectedRowModel().rows.length > 0 && (
        <span className="mr-auto">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </span>
      )}

      <span className="hidden sm:inline">Rows per page</span>
      {/*
        Job A — CHOOSE (`docs/picker-taxonomy.md` §2.1). `variant="compact"`
        matches the 32px pagination row; the width comes from this parent, never
        from a class on the trigger, which is what retires the `w-[62px]`
        arbitrary value (§9.3-16).

        `compact` is `inline-flex … max-w-full shrink-0` and carries no width of
        its own — unlike `field` / `inline`, it does *not* stretch. So the
        trigger has to be told to fill the parent, or the wrapper's width is
        inert and the control collapses to the width of "10".
      */}
      <div className="w-16 shrink-0">
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={(value) => table.setPageSize(Number(value))}
        >
          <SelectTrigger
            variant="compact"
            className="w-full"
            aria-label="Rows per page"
          >
            <SelectValue placeholder={table.getState().pagination.pageSize} />
          </SelectTrigger>
          <SelectContent side="top">
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={`${size}`}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <span className="whitespace-nowrap">
        Page {table.getState().pagination.pageIndex + 1} of{" "}
        {table.getPageCount()}
      </span>

      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="First page"
          className="hidden md:inline-grid"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronsLeft aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Previous page"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronLeft aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Next page"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <ChevronRight aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Last page"
          className="hidden md:inline-grid"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          <ChevronsRight aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

DataTablePagination.displayName = "DataTablePagination";
